import { useCallback, useState } from "react";

import {
  COMMAND_LINK_API_TOKEN_QUERY_PARAM_KEY,
  SUPPORT_EMAIL,
} from "../../../constants";
import { useUnauthenticatedSwrFetch } from "../../../hooks/useUnauthenticatedSwrFetch";

import type { AxiosError } from "axios";

const PREVIEW_ERROR_MESSAGE = `Something went wrong. Please try again or contact ${SUPPORT_EMAIL}.`;

interface PreviewFailure {
  status: number | null;
  code: string | null;
}

interface UseCommandLinkPreviewProps {
  commandName: string;
  token: string;
  /**
   * When false the preview is not fetched at all. Landing pages that act on
   * the preview without user input use this to hold the request until the
   * recipient asks for it, so that anything which merely loads the URL (email
   * security scanners render links in a headless browser) never receives the
   * preview payload.
   */
  enabled?: boolean;
}

interface UseCommandLinkPreviewResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  // The HTTP status of the failed preview, null while loading or on success, and
  // null too when the request never reached the backend. Callers use it to tell a
  // rejected link (403) from a transport or server failure; the 403 body is empty
  // by convention, so the message cannot carry that distinction.
  failureStatus: number | null;
  /**
   * Axios' own error code, which separates the ways a null failureStatus can
   * come about. Axios 0.26 (the installed major) sets one only for an aborted or
   * timed-out request — `ECONNABORTED` either way, or `ETIMEDOUT` when
   * clarifyTimeoutError is on. It passes null for a network/CORS failure and for
   * an HTTP status error, so the ERR_* codes of axios 1.x never appear here.
   *
   * Read it with the status. A null status with no code at all is an API that
   * could not be reached. A null status with `ECONNABORTED` is an *aborted*
   * request — a page the visitor left before the response landed — and that
   * reading holds only because no timeout is configured anywhere: axios defaults
   * to `timeout: 0`, which leaves `XMLHttpRequest.timeout` at 0, so the browser
   * never fires `ontimeout` and the timeout branch cannot be reached. Setting a
   * timeout on this client would fold slow responses into the same code and make
   * the two indistinguishable.
   */
  failureCode: string | null;
}

/**
 * shouldRetryOnError is disabled because a failed token verification (expired,
 * tampered) will never succeed on retry. Without this, SWR retries indefinitely
 * and keeps isLoading true between attempts, trapping the UI in a loading state.
 *
 * onError + the failure state below track errors independently of SWR's returned
 * error state.
 * SWR does not reliably expose the error on the same render cycle that isLoading
 * clears after an initial fetch failure, so the component never sees it. The
 * onError callback fires synchronously on failure and sets React state that
 * we control, guaranteeing the error surfaces to consumers.
 */
export function useCommandLinkPreview<T>(
  props: UseCommandLinkPreviewProps,
): UseCommandLinkPreviewResult<T> {
  const { commandName, token, enabled = true } = props;
  const url = enabled
    ? `/command-links/preview?command_name=${encodeURIComponent(commandName)}&${COMMAND_LINK_API_TOKEN_QUERY_PARAM_KEY}=${encodeURIComponent(token)}`
    : null;
  /**
   * Held as one object rather than a flag plus two fields: the three are always
   * written together, and "the fetch failed" cannot be read off the status and
   * code alone — both are legitimately null when the request never reached the
   * backend.
   */
  const [failure, setFailure] = useState<PreviewFailure | null>(null);

  const handleFetchError = useCallback((requestError: AxiosError) => {
    setFailure({
      status: requestError.response?.status ?? null,
      code: requestError.code ?? null,
    });
  }, []);

  const { data, error } = useUnauthenticatedSwrFetch<T>({
    url,
    errorMessage: PREVIEW_ERROR_MESSAGE,
    errorMessageKey: "detail",
    swrOptions: { shouldRetryOnError: false },
    onError: handleFetchError,
  });

  const resolvedError =
    error ?? (failure != null ? PREVIEW_ERROR_MESSAGE : null);
  const hasResult = data !== undefined || resolvedError !== null;
  return {
    data: data ?? null,
    loading: enabled && !hasResult,
    error: resolvedError,
    failureStatus: failure?.status ?? null,
    failureCode: failure?.code ?? null,
  };
}
